// AssoRelances.jsx — Pipeline IA de relances cotisations
// Workflow : scan éligibles → sélection → génération Claude → revue/édition → marquer envoyée (mock SendGrid)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/usePermissions';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Lock, Check, X, AlertTriangle, Clock, Mail, Send, Trash2, Pencil, RefreshCw, Save,
} from 'lucide-react';

const URGENCE_META = {
  expire:   { label: 'Expiré',     color: 'bg-rose-100 text-rose-700' },
  critique: { label: '≤ 7 jours',  color: 'bg-amber-100 text-amber-700' },
  a_venir:  { label: '≤ 30 jours', color: 'bg-blue-100 text-blue-700' },
};

const STATUT_META = {
  brouillon: { label: 'Brouillon',  color: 'bg-[#F1F5F9] text-[#0F172A]', icon: Pencil },
  approuvee: { label: 'Approuvée',  color: 'bg-emerald-100 text-emerald-700', icon: Check },
  envoyee:   { label: 'Envoyée',    color: 'bg-blue-100 text-blue-700', icon: Send },
  refusee:   { label: 'Refusée',    color: 'bg-rose-100 text-rose-700', icon: X },
};

export default function AssoRelances() {
  const { can, loading: permsLoading } = usePermissions();
  const [tab, setTab] = useState('eligibles');

  if (permsLoading) return <div className="p-8" />;
  if (!can('membres', 'list')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="relances-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès refusé</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="relances-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2 flex items-center gap-2" style={{ color: 'var(--theme-primary)' }}>
          <Sparkles className="w-4 h-4" /> Pipeline IA
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Relances cotisations</h1>
        <p className="text-sm text-[#475569] mt-2">Détection automatique des adhésions à renouveler + génération de messages personnalisés par IA, à valider avant envoi.</p>
      </div>

      <div className="border-b border-[#E2E8F0] mb-4 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 sm:gap-2 min-w-max">
          {[
            { id: 'eligibles', label: 'Éligibles', icon: AlertTriangle },
            { id: 'brouillons', label: 'Brouillons IA', icon: Pencil },
            { id: 'historique', label: 'Historique', icon: Mail },
          ].map((t) => {
            const Ic = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${active ? 'text-[#0F172A]' : 'text-[#475569] border-transparent'}`}
                style={active ? { borderColor: 'var(--theme-primary)' } : {}}
                data-testid={`relances-tab-${t.id}`}
              >
                <Ic className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'eligibles' && <EligiblesView onGenerated={() => setTab('brouillons')} />}
      {tab === 'brouillons' && <BrouillonsView />}
      {tab === 'historique' && <HistoriqueView />}
    </div>
  );
}

function EligiblesView({ onGenerated }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/relances/eligibles');
      setList(data); setSelected(new Set());
    } catch { toast.error('Erreur scan'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleAll = () => {
    if (selected.size === list.filter((l) => !l.deja_relance).length) setSelected(new Set());
    else setSelected(new Set(list.filter((l) => !l.deja_relance).map((l) => l.id)));
  };

  const generate = async () => {
    if (selected.size === 0) return toast.error('Sélectionnez au moins un adhérent');
    setGenerating(true);
    try {
      const { data } = await api.post('/relances/generer', { adhesion_ids: [...selected] });
      toast.success(`${data.created} message(s) IA généré(s)`);
      if (data.errors?.length) console.warn('Errors:', data.errors);
      onGenerated();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur génération'); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  if (list.length === 0) {
    return (
      <div className="card text-center py-12 text-sm text-[#475569]" data-testid="eligibles-empty">
        <Check className="w-8 h-8 mx-auto mb-3 text-emerald-500" />
        Aucune adhésion à relancer pour le moment. Tout est à jour !
      </div>
    );
  }

  const nonRelances = list.filter((l) => !l.deja_relance);

  return (
    <div data-testid="eligibles-view">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#475569]">
          <strong>{list.length}</strong> adhérent(s) éligible(s) {nonRelances.length < list.length && <span className="text-[#94A3B8]">· {list.length - nonRelances.length} déjà relancé(s) récemment</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary text-xs flex items-center gap-1" data-testid="eligibles-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </button>
          <button
            onClick={generate}
            disabled={selected.size === 0 || generating}
            className="btn-primary text-xs flex items-center gap-1"
            data-testid="eligibles-generate"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Générer {selected.size > 0 ? `(${selected.size})` : ''} message(s) IA
          </button>
        </div>
      </div>

      <div className="card !p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F9F8] text-[10px] uppercase tracking-wider text-[#475569] font-bold">
            <tr>
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === nonRelances.length}
                  onChange={toggleAll}
                  data-testid="eligibles-check-all"
                />
              </th>
              <th className="p-2 text-left">Adhérent</th>
              <th className="p-2 text-left hidden sm:table-cell">Formule</th>
              <th className="p-2 text-left">Échéance</th>
              <th className="p-2 text-left">Urgence</th>
            </tr>
          </thead>
          <tbody>
            {list.map((adh) => {
              const u = URGENCE_META[adh.urgence];
              return (
                <tr key={adh.id} className={`border-t border-[#F1F5F9] ${adh.deja_relance ? 'opacity-50' : 'hover:bg-[#F9F9F8]'}`} data-testid={`eligible-row-${adh.id}`}>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      disabled={adh.deja_relance}
                      checked={selected.has(adh.id)}
                      onChange={(e) => {
                        const s = new Set(selected);
                        if (e.target.checked) s.add(adh.id); else s.delete(adh.id);
                        setSelected(s);
                      }}
                      data-testid={`eligible-check-${adh.id}`}
                    />
                  </td>
                  <td className="p-2">
                    <div className="font-bold text-[#0F172A]">{adh.user_nom}</div>
                    <div className="text-xs text-[#475569]">{adh.user_email}</div>
                  </td>
                  <td className="p-2 text-xs text-[#0F172A] hidden sm:table-cell">{adh.label}</td>
                  <td className="p-2 text-xs text-[#0F172A]">
                    {adh.date_fin && new Date(adh.date_fin).toLocaleDateString('fr-FR')}
                    <div className="text-[10px] text-[#94A3B8]">
                      {adh.jours_restants < 0 ? `il y a ${Math.abs(adh.jours_restants)}j` : `dans ${adh.jours_restants}j`}
                    </div>
                  </td>
                  <td className="p-2">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${u.color}`}>{u.label}</span>
                    {adh.deja_relance && <div className="text-[10px] text-[#94A3B8] mt-1">déjà relancé</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BrouillonsView() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/relances?statut=brouillon').then((r) => setList(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  if (list.length === 0) {
    return (
      <div className="card text-center py-12 text-sm text-[#475569]" data-testid="brouillons-empty">
        <Pencil className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
        Aucun brouillon. Allez dans l'onglet <strong>Éligibles</strong> pour générer des messages IA.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="brouillons-view">
      {list.map((r) => <RelanceCard key={r.id} relance={r} onChanged={load} onEdit={() => setEditing(r)} />)}
      {editing && (
        <RelanceEditor relance={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function HistoriqueView() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/relances').then((r) => setList(r.data.filter((x) => ['envoyee', 'refusee', 'approuvee'].includes(x.statut)))).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;
  if (list.length === 0) return <div className="card text-center py-12 text-sm text-[#475569]" data-testid="historique-empty">Aucun historique.</div>;

  return (
    <div className="space-y-3" data-testid="historique-view">
      {list.map((r) => <RelanceCard key={r.id} relance={r} readOnly />)}
    </div>
  );
}

function RelanceCard({ relance, onChanged, onEdit, readOnly }) {
  const meta = STATUT_META[relance.statut] || STATUT_META.brouillon;
  const Ic = meta.icon;

  const approve = async () => {
    try { await api.patch(`/relances/${relance.id}`, { statut: 'approuvee' }); toast.success('Approuvée'); onChanged?.(); }
    catch { toast.error('Erreur'); }
  };
  const refuse = async () => {
    if (!window.confirm('Refuser ce brouillon ?')) return;
    try { await api.patch(`/relances/${relance.id}`, { statut: 'refusee' }); toast.success('Refusée'); onChanged?.(); }
    catch { toast.error('Erreur'); }
  };
  const remove = async () => {
    if (!window.confirm('Supprimer définitivement ?')) return;
    try { await api.delete(`/relances/${relance.id}`); toast.success('Supprimée'); onChanged?.(); }
    catch { toast.error('Erreur'); }
  };
  const markSent = async () => {
    if (!window.confirm('Marquer comme envoyée ? (envoi réel SendGrid bientôt disponible)')) return;
    try {
      await api.post(`/relances/${relance.id}/marquer-envoyee`);
      toast.success('Marquée envoyée (mock SendGrid)'); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
  };

  return (
    <div className="card" data-testid={`relance-${relance.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded flex items-center gap-1 ${meta.color}`}>
              <Ic className="w-3 h-3" /> {meta.label}
            </span>
            <span className="text-xs text-[#475569]">{new Date(relance.created_at).toLocaleString('fr-FR')}</span>
          </div>
          <div className="font-bold text-[#0F172A]">{relance.user_nom}</div>
          <div className="text-xs text-[#475569]">{relance.user_email} · {relance.formule}</div>
        </div>
        {!readOnly && relance.statut === 'brouillon' && (
          <div className="flex flex-wrap gap-1 shrink-0">
            <button onClick={onEdit} className="btn-secondary text-xs flex items-center gap-1" data-testid={`relance-edit-${relance.id}`}><Pencil className="w-3 h-3" /> Éditer</button>
            <button onClick={approve} className="btn-secondary text-xs flex items-center gap-1 text-emerald-700" data-testid={`relance-approve-${relance.id}`}><Check className="w-3 h-3" /> Approuver</button>
            <button onClick={refuse} className="btn-secondary text-xs flex items-center gap-1 text-rose-600" data-testid={`relance-refuse-${relance.id}`}><X className="w-3 h-3" /> Refuser</button>
          </div>
        )}
        {!readOnly && relance.statut === 'approuvee' && (
          <div className="flex flex-wrap gap-1 shrink-0">
            <button onClick={markSent} className="btn-primary text-xs flex items-center gap-1" data-testid={`relance-send-${relance.id}`}><Send className="w-3 h-3" /> Marquer envoyée</button>
          </div>
        )}
      </div>

      <div className="bg-[#F9F9F8] rounded-lg p-3">
        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-1">Objet</div>
        <div className="text-sm font-medium text-[#0F172A] mb-3">{relance.subject}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-1">Message</div>
        <pre className="text-xs text-[#0F172A] whitespace-pre-wrap font-sans">{relance.message}</pre>
      </div>

      {!readOnly && ['brouillon', 'refusee'].includes(relance.statut) && (
        <div className="flex justify-end mt-3">
          <button onClick={remove} className="text-xs text-rose-500 hover:text-rose-700 flex items-center gap-1" data-testid={`relance-delete-${relance.id}`}><Trash2 className="w-3 h-3" /> Supprimer</button>
        </div>
      )}
    </div>
  );
}

function RelanceEditor({ relance, onClose, onSaved }) {
  const [subject, setSubject] = useState(relance.subject);
  const [message, setMessage] = useState(relance.message);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/relances/${relance.id}`, { subject, message });
      toast.success('Modifications enregistrées');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-start justify-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <div className="card max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="relance-editor">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Éditer la relance — {relance.user_nom}</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Objet</label>
            <input
              type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              maxLength={200} className="input-base" data-testid="editor-subject"
            />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Message</label>
            <textarea
              rows={12} value={message} onChange={(e) => setMessage(e.target.value)}
              maxLength={5000} className="input-base font-mono text-xs"
              data-testid="editor-message"
            />
            <div className="text-[10px] text-[#94A3B8] mt-1">{message.length}/5000 caractères</div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1" data-testid="editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
